import { useState } from 'react'
import SubHeaders from '@/components/Headers/SubHeaders'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { getComponent, deleteItem } from '../../services'
import { paths } from '@/utils/path'
import { useParams, useNavigate } from 'react-router-dom'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { GridInformation, Tabs, Button } from 'vnetwork-ui'
import { Badge } from '@/components/ui/badge'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import useTabs from '@/hooks/useTabs'
import { getStatusBadgeVariant, formatDateTime } from '../../utils'
import { tabs } from './tabs'
import { Trash2 } from 'lucide-react'

const ComponentDetail = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

    const { data: component, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_COMPONENTS, id],
        queryFn: () => getComponent(id!),
    })

    // Delete mutation
    const { mutate: remove, isPending: isDeleting } = useMutationWithHandler(
        {
            mutationFn: () => deleteItem(id!),
            onSuccess: () => {
                setDeleteDialogOpen(false)
                navigate(paths.warehouseItems)
            },
        },
        {
            successMessage: 'Component deleted',
            errorMessage: 'Failed to delete component',
            reactQueryKeys: QUERY_KEYS.WAREHOUSE_ITEMS,
        }
    )

    const { TabComponent, TabProps } = useTabs({ tabs: tabs() })

    // Render loading/error state at the end (after all hooks)
    if (renderState()) return renderState()

    return (
        <div>
            <SubHeaders
                actions={[
                    {
                        label: 'Delete',
                        icon: <Trash2 size={16} />,
                        onClick: () => setDeleteDialogOpen(true),
                        variant: 'outlined',
                        color: 'error',
                        disabled: isDeleting,
                    },
                ]}
                breadcrumbs={[
                    {
                        path: paths.warehouse,
                        title: 'Capacities & Resources',
                    },
                    {
                        path: `${paths.warehouseItems}?tab=${component?.category || 'ram'}`,
                        title: 'Items',
                    },
                    {
                        path: '',
                        title: component?.name || '',
                    },
                ]}
            />
            <GridInformation
                data={[
                    {
                        label: 'Component Name',
                        value: component?.name || '-',
                    },
                    {
                        label: 'Ref',
                        value: component?.ref || '-',
                    },
                    {
                        label: 'Serial Number',
                        value: component?.serial_number || '-',
                        hidden: !component?.serial_number,
                    },
                    {
                        label: 'Category',
                        value: component?.category || '-',
                    },
                    {
                        label: 'Status',
                        value: (
                            <Badge
                                variant={getStatusBadgeVariant(
                                    component?.status || ''
                                )}
                            >
                                {component?.status?.toUpperCase() || ''}
                            </Badge>
                        ),
                    },
                    {
                        label: 'Condition',
                        value: component?.condition || '-',
                    },
                    {
                        label: 'Location',
                        value: component?.current_location?.name || '-',
                        hidden: !component?.current_location,
                    },
                    {
                        label: 'Parent Equipment',
                        value: component?.parent_item?.name || '-',
                        hidden: !component?.parent_item,
                    },
                    {
                        label: 'Model',
                        value: component?.model?.name || '-',
                        hidden: !component?.model,
                    },
                    {
                        label: 'Supplier',
                        value: component?.supplier?.name || '-',
                        hidden: !component?.supplier,
                    },
                    {
                        label: 'Purchase Date',
                        value: component?.purchase_date
                            ? formatDateTime(component.purchase_date)
                            : '-',
                        hidden: !component?.purchase_date,
                    },
                    {
                        label: 'Purchase Price',
                        value: component?.purchase_price
                            ? `${component.purchase_price.toLocaleString()} ${component.purchase_currency || 'VND'}`
                            : '-',
                        hidden: !component?.purchase_price,
                    },
                    {
                        label: 'Owner',
                        value: component?.owner || '-',
                        hidden: !component?.owner,
                    },
                    {
                        label: 'Description',
                        value: component?.description || '-',
                        className: 'lg:col-span-2',
                        hidden: !component?.description,
                    },
                    {
                        label: 'Notes',
                        value: component?.notes || '-',
                        className: 'lg:col-span-2',
                        hidden: !component?.notes,
                    },
                    {
                        label: 'Created At',
                        value: formatDateTime(component?.created_at),
                    },
                    {
                        label: 'Updated At',
                        value: formatDateTime(component?.updated_at),
                    },
                ]}
                head={{
                    title: 'Component Information',
                }}
            />

            <Tabs {...TabProps} />

            {TabComponent}

            {/* Delete confirmation dialog */}
            <Dialog
                open={deleteDialogOpen}
                onOpenChange={(open) => setDeleteDialogOpen(open)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Component</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this component?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outlined"
                            onClick={() => setDeleteDialogOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => remove()}
                            disabled={isDeleting}
                        >
                            Delete Component
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default ComponentDetail
